import { Component, Input, EventEmitter, Output, OnInit } from '@angular/core';
import { MatSliderChange } from '@angular/material';

import { MapifyMap } from '../../models/MapifyMap';
import { Coordinate } from '../../models/Coordinate';
import { Icon, IconType } from '../../models/Icon';
import { Style } from '../../models/Style';
import { Orientation } from '../../models/Orientation';
import { Finish } from '../../models/Finish';
import { environment } from '../../../environments/environment';
import { DomSanitizer } from '@angular/platform-browser';

interface IconSize {
  width: number;
  height: number;
}

interface IconSelector {
  url: string;
  type: IconType;
}

const defaultSize = 30;
@Component({
  selector: 'mpf-map-config-icons',
  templateUrl: './map-config-icons.component.html',
  styleUrls: ['./map-config-icons.component.scss']
})
export class MapConfigIconsComponent implements OnInit {
  iconSelectors: IconSelector[] = [];

  maxSize = 80;
  minSize = 10;
  step = 1;
  size = defaultSize;

  selectedIcon: IconSelector | null = null;

  private _mapStyle: Style;
  private _mapCenter: Coordinate;

  @Input() map: MapifyMap;
  @Input() mapIcons: Icon[];
  @Output() mapIconsChange = new EventEmitter<Icon[]>();

  @Input()
  set mapStyle(style: Style) {
    this._mapStyle = style;

    this.setIconSelectors(this.mapStyle, this.map.size.orientation, this.map.finish, this.map.center);
  }

  get mapStyle(): Style {
    return this._mapStyle;
  }

  @Input()
  set mapCenter(center: Coordinate) {
    this._mapCenter = center;

    this.setIconSelectors(this.map.style, this.map.size.orientation, this.map.finish, this.map.center);
  }

  get mapCenter(): Coordinate {
    return this._mapCenter;
  }

  constructor(public sanitizer: DomSanitizer) {}

  ngOnInit() {
    this.setIconSelectors(this.mapStyle, this.map.size.orientation, this.map.finish, this.map.center);
  }

  setIconSelectors(style: Style, orientation: Orientation, finish: Finish, center: Coordinate) {
    const iconsConfig = environment.mapify.mapStyle[style.name][orientation][finish.id].icons;

    this.iconSelectors = Object.keys(iconsConfig).reduce((acc: IconSelector[], key: IconType) => {
      const url: string = iconsConfig[key].url;

      return [...acc, { url, type: key }];
    }, []);
  }

  onIconSelected(iconSelector: IconSelector) {
    this.selectedIcon = iconSelector;
    this.onSizeSelected(iconSelector, { width: this.size, height: this.size });
  }

  sizeChanged(sliderChange: MatSliderChange) {
    const size = sliderChange.value;
    if (this.selectedIcon !== null && size !== null) {
      this.size = size;
      this.onSizeSelected(this.selectedIcon, { width: size, height: size });
    }
  }

  onSizeSelected(iconSelector: IconSelector, size: IconSize) {
    this.mapIconsChange.emit([{ ...iconSelector, size }]);
  }

  onClearSelection() {
    this.size = defaultSize;
    this.selectedIcon = null;
    this.mapIconsChange.emit([]);
  }

  getStyleUrl(selectedIcon: IconSelector | null) {
    if (selectedIcon !== null) {
      return `url(${selectedIcon.url})`;
    }
  }

  isIconSelected(iconSelector: IconSelector): boolean {
    return this.selectedIcon !== null && this.selectedIcon.type === iconSelector.type;
  }

  iconContainerBackground() {
    const { name } = this.mapStyle;

    // some styles have white icons so we have to adjust the background to make them visible
    const color = [
      'classic-chrome',
      'classic-black',
      'blueprint-no-labels',
      'blueprint-deep-no-labels',
      'blueprint-green-no-labels',
      'blueprint-red-no-labels',
      'blueprint-yellow-no-labels',
      'blueprint-pink-no-labels'
    ].includes(name)
      ? '#eee'
      : '#fff';

    return this.sanitizer.bypassSecurityTrustStyle(color);
  }
}



// WEBPACK FOOTER //
// /vercel/workpath0/src/app/components/map-config-icons/map-config-icons.component.ts